import type { z } from "zod";
import type { StepConfig } from "./index";
import type { SystemEvent } from "@erp/shared-types";

/**
 * Helper function to create a workflow step/tool config with full type inference.
 *
 * The parameter type IS {@link StepConfig} — do not re-declare its fields here.
 * This used to spell the whole field list out a second time, which is a ~60-line
 * copy that silently drifts every time a field is added on one side only (fallow
 * flagged it as a clone group). Inference is unaffected: `TInput`/`TOutput`/
 * `TConfig`/`TEmits` are inferred from the same `inputSchema`/`outputSchema`/
 * `configSchema`/`emits` properties either way.
 *
 * defineStepConfig is an alias — keep a single implementation to avoid
 * step vs tool divergence. Any "step-only" fields (like `deprecated`) are
 * optional and harmless on tool configs.
 */
export function defineToolConfig<
  TConfig extends z.ZodType,
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
  TEmits extends readonly SystemEvent[] = readonly [],
>(
  params: StepConfig<TConfig, TInput, TOutput, TEmits>,
): StepConfig<TConfig, TInput, TOutput, TEmits> {
  return params;
}

// DO NOT split defineStepConfig and defineToolConfig into separate functions.
// Steps and AI tools are converging on the same registration/config mechanism.
// If a tool type isn't in StepTypeId yet, add it there — don't fork the helper.
export const defineStepConfig = defineToolConfig;
